/**
 * «Revisar el dibujo» (OPUS ci, solo staff): antes de presupuestar un DWG/DXF, avisa de lo que del dibujo
 * produce cantidades equivocadas en la hoja (piezas o trazos repetidos encima, trazos sueltos, unidad
 * contradictoria, cotas que no cuadran, bloques a otra escala o duplicados con dos nombres).
 *
 * $0 y determinista: nada de IA, ni listas de nombres de capa o bloque. Todo criterio sale de la coherencia
 * interna del propio dibujo (estadística por vista, por bloque, por capa) y los umbrales `diag_*` viven en
 * Calibración (`ParametrosChequeoPlano`). No cambia cantidades: solo avisa y señala. Spec:
 * `docs/_archivo/internos/spec-diagnostico-dibujo.md`.
 *
 * Las coordenadas van en UNIDADES DEL DIBUJO (como `PlanoVista.caja`): así funciona aunque la escala sea
 * dudosa, y cambiar la unidad solo recalcula impactos.
 */
import type { DecisionDeCapa, PlanoInventario } from './domain.js';
import type { FactorDeEscala } from './escalaDelPlano.js';
import type { ParametrosChequeoPlano } from './chequeoDelPlano.js';

/** Una entidad de la modelspace, compacta (la arma el lector del navegador). Coordenadas en unidades del dibujo. */
export interface EntidadDelDibujo {
  handle: string;
  /** LINE, LWPOLYLINE, POLYLINE, ARC, CIRCLE, ELLIPSE, INSERT, TEXT, MTEXT, DIMENSION, HATCH, SPLINE, POINT… */
  tipo: string;
  capa: string;
  /** Caja [x0, y0, x1, y1]. */
  caja: readonly [number, number, number, number];
  /** Largo del trazo (líneas, polilíneas, arcos, círculos); 0 si no aplica. */
  largo: number;
  /** Puntos normalizados. Ausente en textos/cotas/achurados y en polilíneas largas (ver `huella`). */
  puntos?: readonly (readonly [number, number])[];
  /** Huella ya calculada de un trazo con muchos vértices (se descartan sus puntos para ahorrar memoria). */
  huella?: number;
  /** Solo INSERT: nombre crudo (anónimos incluidos), rotación en rad y escala con signo. */
  bloque?: { crudo: string; rot: number; sx: number; sy: number };
}

/** Huella de la definición de un bloque. `huella` null = no se pudo o pasa del tope de trazos. */
export interface HuellaDeBloque {
  crudo: string;
  /** Nombre limpio como el del inventario (`PlanoBloque.nombre`). */
  limpio: string;
  huella: string | null;
  trazos: number;
}

export type ClaveDeAviso =
  | 'piezas_repetidas'
  | 'trazos_repetidos'
  | 'sueltos'
  | 'unidad'
  | 'cotas'
  | 'escala_de_bloque'
  | 'bloques_gemelos';

export type GravedadDeAviso = 'alta' | 'media' | 'info';

export interface AvisoDelDibujo {
  clave: ClaveDeAviso;
  gravedad: GravedadDeAviso;
  /** Una línea: «12 piezas repetidas una encima de otra». */
  titulo: string;
  quePasa: string;
  porQueAfecta: string;
  queHacer: string;
  /** Cuántas piezas toca (se muestra como «Toca N piezas»). */
  piezas: number;
  capas: string[];
  bloques: string[];
  /** Para «Ver en el plano» (tope `TOPE_HANDLES_POR_AVISO`; `piezas` cuenta todas). */
  handles: string[];
  /** Cotas: puntos en unidades del dibujo (no tienen handle en el inventario). */
  puntos?: { x: number; y: number }[];
  /** Lo que se cuenta de más en la hoja, si se puede medir. */
  impacto?: { magnitud: 'pza' | 'ml' | 'm2' | 'm3'; cantidad: number; pctDeLaCapa?: number } | null;
}

export interface DiagnosticoDelDibujo {
  /** Orden: gravedad (alta → info) y luego piezas, de más a menos. */
  avisos: AvisoDelDibujo[];
  entidadesRevisadas: number;
  /** true = no hubo entidades del visor: solo los chequeos que salen del inventario. */
  parcial: boolean;
}

export interface EntradaDiagnostico {
  inventario: PlanoInventario;
  escala: FactorDeEscala;
  entidades: readonly EntidadDelDibujo[] | null;
  huellas: readonly HuellaDeBloque[] | null;
  decisionCapa?: (capa: string) => DecisionDeCapa | null;
  decisionBloque?: (nombre: string) => DecisionDeCapa | null;
}

/** Tope de memoria y de pantalla, no regla de negocio. */
export const TOPE_HANDLES_POR_AVISO = 5000;

// ───────────────────────────── utilidades puras ─────────────────────────────

const fmt = (n: number) => n.toLocaleString('es-MX', { maximumFractionDigits: 1 });
const piezasTxt = (n: number) => (n === 1 ? '1 pieza' : `${fmt(n)} piezas`);

/** Tolerancia en unidades del dibujo: la de calibración en metros / factor; sin factor, relativa al tamaño del dibujo. */
export function toleranciaEnUnidades(factor: number | null, diagonal: number, p: ParametrosChequeoPlano): number {
  const t = factor != null && factor > 0 ? p.diag_duplicado_tol_m / factor : diagonal * p.diag_tol_relativa;
  return t > 0 && Number.isFinite(t) ? t : 1e-9;
}

function fnv(s: string, semilla: number): number {
  let h = semilla >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

/** Hash de 53 bits de un texto (dos FNV-1a con semillas distintas). */
function hash53(s: string): number {
  const lo = fnv(s, 2166136261);
  const hi = fnv(s, 0x811c9dc5 ^ 0x9e3779b9) & 0x1fffff;
  return hi * 4294967296 + lo;
}

/**
 * Huella de un trazo: tipo + capa + puntos cuantizados a `tol`. Es la misma para el trazo recorrido al revés
 * (se toma la menor de las dos orientaciones). Dos puntos a menos de `tol` pueden caer en celdas distintas;
 * se acepta (el chequeo avisa de más, nunca de menos, en lo que sí coincide exacto).
 */
export function huellaGeometrica(
  tipo: string,
  capa: string,
  puntos: readonly (readonly [number, number])[],
  tol: number,
): number {
  const t = tol > 0 ? tol : 1e-9;
  const q = puntos.map(([x, y]) => `${Math.round(x / t)},${Math.round(y / t)}`);
  const a = q.join(';');
  const b = [...q].reverse().join(';');
  return hash53(`${tipo}|${capa}|${a < b ? a : b}`);
}

/**
 * Huella de la DEFINICIÓN de un bloque: sus trazos, trasladados al origen de su caja y divididos por su
 * diagonal (iguales aunque estén en otro lugar o a otra escala uniforme), cuantizados a `tolRel` de la
 * diagonal, ordenados y hasheados. null si no hay trazos o la caja es un punto. No iguala espejos ni giros.
 */
export function huellaDeDefinicion(
  trazos: readonly { tipo: string; puntos: readonly (readonly [number, number])[] }[],
  tolRel: number,
): string | null {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  let n = 0;
  for (const t of trazos) {
    for (const [x, y] of t.puntos) {
      if (x < x0) x0 = x;
      if (y < y0) y0 = y;
      if (x > x1) x1 = x;
      if (y > y1) y1 = y;
      n++;
    }
  }
  const diag = Math.hypot(x1 - x0, y1 - y0);
  if (!n || !(diag > 0)) return null;
  const tol = tolRel > 0 ? tolRel : 0.01;
  const claves = trazos
    .filter((t) => t.puntos.length > 0)
    .map((t) => {
      const q = t.puntos.map(([x, y]) => `${Math.round((x - x0) / diag / tol)},${Math.round((y - y0) / diag / tol)}`);
      const a = q.join(';');
      const b = [...q].reverse().join(';');
      return `${t.tipo}|${a < b ? a : b}`;
    })
    .sort();
  if (!claves.length) return null;
  return `${claves.length}:${hash53(claves.join('#')).toString(36)}`;
}

const diagonalDelDibujo = (inv: PlanoInventario, ents: readonly EntidadDelDibujo[] | null): number => {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  const cajas: readonly (readonly [number, number, number, number])[] = inv.vistas?.length
    ? inv.vistas.map((v) => v.caja)
    : (ents ?? []).slice(0, 5000).map((e) => e.caja);
  for (const c of cajas) {
    x0 = Math.min(x0, c[0]);
    y0 = Math.min(y0, c[1]);
    x1 = Math.max(x1, c[2]);
    y1 = Math.max(y1, c[3]);
  }
  const d = Math.hypot(x1 - x0, y1 - y0);
  return Number.isFinite(d) ? d : 0;
};

const sePuedeCotizar = (e: EntradaDiagnostico, capa: string): boolean => e.decisionCapa?.(capa) !== 'ruido';
const bloqueSeCotiza = (e: EntradaDiagnostico, nombre: string): boolean => e.decisionBloque?.(nombre) !== 'ruido';

function nombresPorCrudo(inv: PlanoInventario): Map<string, string> {
  const m = new Map<string, string>();
  for (const b of inv.bloques) m.set(b.nombre_crudo, b.nombre);
  return m;
}

function idsQueSuman(inv: PlanoInventario): Set<string> {
  const s = new Set<string>();
  for (const b of inv.bloques) for (const id of b.ids) s.add(id);
  return s;
}

const topeHandles = (h: string[]) => (h.length > TOPE_HANDLES_POR_AVISO ? h.slice(0, TOPE_HANDLES_POR_AVISO) : h);

const lista = (xs: string[], max = 3) => {
  const u = [...new Set(xs)];
  return u.length <= max ? u.join(', ') : `${u.slice(0, max).join(', ')} y ${u.length - max} más`;
};

interface Nucleo {
  caja: [number, number, number, number];
  ancho: number;
  alto: number;
  n: number;
}

const percentil = (orden: Float64Array, p: number): number => {
  if (!orden.length) return 0;
  const i = Math.min(orden.length - 1, Math.max(0, Math.round((p / 100) * (orden.length - 1))));
  return orden[i] ?? 0;
};

type Caja = readonly [number, number, number, number];

const distanciaCajas = (a: Caja, b: Caja): number => {
  const dx = Math.max(0, a[0] - b[2], b[0] - a[2]);
  const dy = Math.max(0, a[1] - b[3], b[1] - a[3]);
  return Math.hypot(dx, dy);
};

/** Asigna cada entidad a la vista cuya caja le queda más cerca y saca el «núcleo» (centro) de cada vista. */
function repartirEnVistas(
  inv: PlanoInventario,
  ents: readonly EntidadDelDibujo[],
  p: ParametrosChequeoPlano,
): { porVista: Map<string, EntidadDelDibujo[]>; nucleos: Map<string, Nucleo> } {
  const vistas = inv.vistas ?? [];
  const porVista = new Map<string, EntidadDelDibujo[]>();
  const nucleos = new Map<string, Nucleo>();
  if (!vistas.length) return { porVista, nucleos };
  for (const v of vistas) porVista.set(v.id, []);
  for (const e of ents) {
    let mejor = vistas[0]!;
    let dm = Infinity;
    for (const v of vistas) {
      const d = distanciaCajas(e.caja, v.caja);
      if (d < dm) {
        dm = d;
        mejor = v;
        if (d === 0) break;
      }
    }
    porVista.get(mejor.id)!.push(e);
  }
  for (const v of vistas) {
    const lst = porVista.get(v.id)!;
    if (lst.length < p.diag_vista_min_entidades) continue;
    const xs = new Float64Array(lst.length);
    const ys = new Float64Array(lst.length);
    lst.forEach((e, i) => {
      xs[i] = (e.caja[0] + e.caja[2]) / 2;
      ys[i] = (e.caja[1] + e.caja[3]) / 2;
    });
    xs.sort();
    ys.sort();
    const c: [number, number, number, number] = [
      percentil(xs, p.diag_nucleo_pct),
      percentil(ys, p.diag_nucleo_pct),
      percentil(xs, 100 - p.diag_nucleo_pct),
      percentil(ys, 100 - p.diag_nucleo_pct),
    ];
    nucleos.set(v.id, { caja: c, ancho: c[2] - c[0], alto: c[3] - c[1], n: lst.length });
  }
  return { porVista, nucleos };
}

// ───────────────────────────── chequeos ─────────────────────────────

/** V1-1 · Bloques dibujados dos veces en el mismo lugar (cada copia suma una pieza). */
export function piezasRepetidas(e: EntradaDiagnostico, p: ParametrosChequeoPlano): AvisoDelDibujo | null {
  const ents = e.entidades;
  if (!ents) return null;
  const tol = toleranciaEnUnidades(e.escala.factor, diagonalDelDibujo(e.inventario, ents), p);
  const grupos = new Map<string, EntidadDelDibujo[]>();
  for (const x of ents) {
    if (!x.bloque) continue;
    const b = x.bloque;
    const cx = (x.caja[0] + x.caja[2]) / 2;
    const cy = (x.caja[1] + x.caja[3]) / 2;
    const k = [
      b.crudo,
      x.capa,
      Math.round(b.rot * 10000),
      Math.round(b.sx * 10000),
      Math.round(b.sy * 10000),
      Math.round(cx / tol),
      Math.round(cy / tol),
    ].join('|');
    const g = grupos.get(k);
    if (g) g.push(x);
    else grupos.set(k, [x]);
  }
  const suman = idsQueSuman(e.inventario);
  const limpio = nombresPorCrudo(e.inventario);
  let repetidas = 0;
  let deMas = 0;
  let seCotiza = false;
  const handles: string[] = [];
  const capas: string[] = [];
  const bloques: string[] = [];
  for (const g of grupos.values()) {
    if (g.length < 2) continue;
    const enIds = g.filter((x) => suman.has(x.handle)).length;
    const g0 = g[0]!;
    const nombre = limpio.get(g0.bloque!.crudo) ?? g0.bloque!.crudo;
    repetidas += g.length - 1;
    const extra = Math.max(0, enIds - 1);
    deMas += extra;
    if (extra > 0 && bloqueSeCotiza(e, nombre)) seCotiza = true;
    for (const x of g.slice(1)) handles.push(x.handle);
    capas.push(g0.capa);
    bloques.push(nombre);
  }
  if (!repetidas) return null;
  const gravedad: GravedadDeAviso = deMas > 0 ? (seCotiza ? 'alta' : 'media') : 'info';
  return {
    clave: 'piezas_repetidas',
    gravedad,
    titulo: `${piezasTxt(repetidas)} repetida${repetidas === 1 ? '' : 's'} una encima de otra (${lista(bloques)})`,
    quePasa: 'Hay bloques dibujados dos veces o más en el mismo lugar.',
    porQueAfecta:
      deMas > 0
        ? `Cada copia se cuenta: en la hoja saldrían ${piezasTxt(deMas)} de más.`
        : 'Están en una vista que no suma, así que no cambian las cantidades; conviene limpiarlas.',
    queHacer: 'Bórralas en tu programa de dibujo y sube el plano otra vez, o corrige la cantidad a mano en la hoja.',
    piezas: repetidas,
    capas: [...new Set(capas)],
    bloques: [...new Set(bloques)],
    handles: topeHandles(handles),
    impacto: deMas > 0 ? { magnitud: 'pza', cantidad: deMas } : null,
  };
}

const TIPOS_DE_TRAZO = new Set(['LINE', 'LWPOLYLINE', 'POLYLINE', 'ARC', 'CIRCLE', 'ELLIPSE']);

/** V1-2 · Trazos (líneas, polilíneas, arcos, círculos) repetidos encima, en la misma capa. */
export function trazosRepetidos(e: EntradaDiagnostico, p: ParametrosChequeoPlano): AvisoDelDibujo[] {
  const ents = e.entidades;
  if (!ents) return [];
  const tol = toleranciaEnUnidades(e.escala.factor, diagonalDelDibujo(e.inventario, ents), p);
  const vistos = new Set<number>();
  const porCapa = new Map<string, { largo: number; n: number; handles: string[] }>();
  for (const x of ents) {
    if (!TIPOS_DE_TRAZO.has(x.tipo) || !(x.largo > 0)) continue;
    const h = x.huella ?? (x.puntos?.length ? huellaGeometrica(x.tipo, x.capa, x.puntos, tol) : null);
    if (h == null) continue;
    if (!vistos.has(h)) {
      vistos.add(h);
      continue;
    }
    const c = porCapa.get(x.capa) ?? { largo: 0, n: 0, handles: [] };
    c.largo += x.largo;
    c.n++;
    c.handles.push(x.handle);
    porCapa.set(x.capa, c);
  }
  if (!porCapa.size) return [];
  const capasInv = new Map(e.inventario.capas.map((c) => [c.capa, c]));
  const factor = e.escala.factor;
  const medidas: AvisoDelDibujo[] = [];
  const ruido: { capa: string; n: number; handles: string[] }[] = [];
  let repetidasMedidas = 0;
  for (const [capa, c] of porCapa) {
    const inv = capasInv.get(capa);
    const fuente = inv?.cuantificacion?.fuente;
    const seMidePorLargo = inv != null && (fuente === 'largo' || (fuente == null && (inv.longitud ?? 0) > 0));
    if (!seMidePorLargo || !sePuedeCotizar(e, capa)) {
      ruido.push({ capa, n: c.n, handles: c.handles });
      continue;
    }
    const pctCapa = (inv!.longitud ?? 0) > 0 ? (c.largo / inv!.longitud!) * 100 : 0;
    const ml = factor != null ? c.largo * factor : null;
    repetidasMedidas += c.n;
    medidas.push({
      clave: 'trazos_repetidos',
      gravedad: pctCapa >= p.diag_repetido_capa_pct ? 'alta' : 'media',
      titulo: `Líneas dibujadas dos veces en la capa «${capa}»`,
      quePasa: `Hay ${piezasTxt(c.n)} de trazo repetido${c.n === 1 ? '' : 's'} una encima de otra.`,
      porQueAfecta:
        ml != null
          ? `En esta capa se miden ${fmt(ml)} m de más (${fmt(pctCapa)} % de la capa).`
          : `Se miden de más (${fmt(pctCapa)} % de la capa).`,
      queHacer: 'Bórralas en tu programa de dibujo y sube el plano otra vez, o corrige la medida a mano en la hoja.',
      piezas: c.n,
      capas: [capa],
      bloques: [],
      handles: topeHandles(c.handles),
      impacto: ml != null ? { magnitud: 'ml', cantidad: ml, pctDeLaCapa: pctCapa } : null,
    });
  }
  const out = medidas.sort((a, b) => b.piezas - a.piezas);
  if (ruido.length) {
    const total = ruido.reduce((s, r) => s + r.n, 0);
    out.push({
      clave: 'trazos_repetidos',
      gravedad: 'info',
      titulo: `Líneas dibujadas dos veces en ${ruido.length === 1 ? '1 capa' : `${ruido.length} capas`}`,
      quePasa: `Hay ${piezasTxt(total)} de trazo repetido${total === 1 ? '' : 's'} una encima de otra.`,
      porQueAfecta: 'No cambia la medida (ya se descartan o no se miden por su largo), pero conviene limpiarlo.',
      queHacer: 'Si quieres un dibujo limpio, bórralas en tu programa de dibujo.',
      piezas: total,
      capas: ruido.map((r) => r.capa),
      bloques: [],
      handles: topeHandles(ruido.flatMap((r) => r.handles)),
      impacto: null,
    });
  }
  void repetidasMedidas;
  return out;
}

/** V1-3 · Trazos o piezas sueltos lejos del plano, o más largos que su propia vista. */
export function piezasSueltas(e: EntradaDiagnostico, p: ParametrosChequeoPlano): AvisoDelDibujo | null {
  const ents = e.entidades;
  if (!ents || !e.inventario.vistas?.length) return null;
  const { porVista, nucleos } = repartirEnVistas(e.inventario, ents, p);
  const vistas = new Map(e.inventario.vistas.map((v) => [v.id, v]));
  const handles: string[] = [];
  const capas = new Set<string>();
  const bloques = new Set<string>();
  const limpio = nombresPorCrudo(e.inventario);
  let total = 0;
  let afecta = false;
  let mlSueltos = 0;
  let inflada = false;
  for (const [vid, nu] of nucleos) {
    const v = vistas.get(vid)!;
    const lado = Math.max(nu.ancho, nu.alto);
    const diag = Math.hypot(nu.ancho, nu.alto);
    if (!(lado > 0)) continue;
    const areaNucleo = Math.max(nu.ancho, 0) * Math.max(nu.alto, 0);
    const areaCaja = Math.max(v.caja[2] - v.caja[0], 0) * Math.max(v.caja[3] - v.caja[1], 0);
    if (areaNucleo > 0 && areaCaja / areaNucleo > p.diag_caja_inflada_ratio) inflada = true;
    for (const x of porVista.get(vid)!) {
      if (!(x.largo > 0) && !x.bloque) continue;
      const lejos = distanciaCajas(x.caja, nu.caja) > p.diag_suelta_factor * lado;
      const desmedido = x.largo > p.diag_trazo_largo_factor * diag;
      if (!lejos && !desmedido) continue;
      total++;
      handles.push(x.handle);
      capas.add(x.capa);
      const nombre = x.bloque ? limpio.get(x.bloque.crudo) ?? x.bloque.crudo : null;
      if (nombre) bloques.add(nombre);
      const cotiza = nombre ? bloqueSeCotiza(e, nombre) : sePuedeCotizar(e, x.capa);
      if (v.suma && cotiza) afecta = true;
      if (v.suma && !x.bloque) mlSueltos += x.largo;
    }
  }
  if (!total) return null;
  const factor = e.escala.factor;
  return {
    clave: 'sueltos',
    gravedad: afecta ? 'alta' : 'info',
    titulo: `${total === 1 ? '1 trazo suelto' : `${fmt(total)} trazos sueltos`} lejos del plano`,
    quePasa: `Hay líneas o piezas muy lejos de todo lo demás, o más largas que la propia planta${inflada ? '; por eso la planta se ve más grande de lo que es' : ''}.`,
    porQueAfecta: 'Se suman a la planta más cercana y la hacen ver más grande.',
    queHacer: 'Revisa si son de la obra; si no, bórralas en tu programa de dibujo y vuelve a subir el plano.',
    piezas: total,
    capas: [...capas],
    bloques: [...bloques],
    handles: topeHandles(handles),
    impacto: afecta && mlSueltos > 0 && factor != null ? { magnitud: 'ml', cantidad: mlSueltos * factor } : null,
  };
}

/** V1-4 · Unidad contradictoria y cotas que no cuadran (solo con el inventario, sin visor). */
export function unidadYCotas(e: EntradaDiagnostico, p: ParametrosChequeoPlano): AvisoDelDibujo[] {
  const inv = e.inventario;
  const out: AvisoDelDibujo[] = [];
  const total = inv.capas.reduce((s, c) => s + c.n_entidades, 0);
  const esc = inv.escala;
  let dice: string | null = null;
  let mide: string | null = null;
  if (esc) {
    if (esc.estado === 'definida' && esc.fuente === 'dibujo' && e.escala.fuente !== 'usuario') {
      const h = esc.header?.unidad?.trim().toLowerCase() ?? null;
      const u = esc.unidad?.trim().toLowerCase() ?? null;
      if (h && u && h !== u) {
        dice = h;
        mide = u;
      }
    }
  } else if (inv.escala_por_cotas?.concuerda_insunits === false && inv.escala_por_cotas.unidad) {
    dice = inv.unidades?.nombre?.trim().toLowerCase() ?? null;
    mide = inv.escala_por_cotas.unidad.trim().toLowerCase();
    if (!dice || dice === mide) dice = mide = null;
  }
  if (dice && mide) {
    out.push({
      clave: 'unidad',
      gravedad: 'media',
      titulo: `El archivo dice ${dice}, pero el dibujo está en ${mide}`,
      quePasa: `El archivo declara «${dice}», y las medidas del propio dibujo dicen «${mide}»; medimos en ${mide}.`,
      porQueAfecta: 'Si medimos con la unidad equivocada, todo sale muchas veces más grande o más chico.',
      queHacer: `Si no es ${mide}, cambia la unidad del plano.`,
      piezas: total,
      capas: [],
      bloques: [],
      handles: [],
      impacto: null,
    });
  }
  const cv = inv.cotas_validacion;
  if (cv && cv.asociadas > 0) {
    const vistas = new Map((inv.vistas ?? []).map((v) => [v.id, v]));
    const malas = cv.no_cuadran_detalle.filter((c) => c.vista == null || vistas.get(c.vista)?.suma !== false);
    const pctMalas = (malas.length / cv.asociadas) * 100;
    if (malas.length >= p.diag_cotas_no_cuadran_min && pctMalas >= p.diag_cotas_no_cuadran_pct) {
      out.push({
        clave: 'cotas',
        gravedad: pctMalas >= 50 ? 'alta' : 'media',
        titulo: `${malas.length === 1 ? '1 cota no coincide' : `${fmt(malas.length)} cotas no coinciden`} con lo que mide${malas.length === 1 ? '' : 'n'}`,
        quePasa: 'El número escrito en la cota no es lo que mide su propio trazo.',
        porQueAfecta:
          'Las cantidades salen del dibujo, no del número escrito: si el dibujo no está a escala en esa parte, la cantidad no es la que dice la cota.',
        queHacer: 'Revisa esas cotas en tu programa de dibujo (fuera de escala o corregidas a mano).',
        piezas: malas.length,
        capas: [],
        bloques: [],
        handles: [],
        puntos: malas.map((c) => ({ x: c.x, y: c.y })),
        impacto: null,
      });
    }
  }
  return out;
}

/** V1-5 · Bloques a otra escala que sus iguales, o más grandes que su propia vista. */
export function escalaDeBloques(e: EntradaDiagnostico, p: ParametrosChequeoPlano): AvisoDelDibujo | null {
  const ents = e.entidades;
  if (!ents) return null;
  const limpio = nombresPorCrudo(e.inventario);
  const porBloque = new Map<string, EntidadDelDibujo[]>();
  for (const x of ents) {
    if (!x.bloque) continue;
    const g = porBloque.get(x.bloque.crudo);
    if (g) g.push(x);
    else porBloque.set(x.bloque.crudo, [x]);
  }
  const moda = (valores: number[]): number | null => {
    const cuenta = new Map<number, number>();
    for (const v of valores) cuenta.set(v, (cuenta.get(v) ?? 0) + 1);
    let mejor: number | null = null;
    let n = 0;
    for (const [v, c] of cuenta) if (c > n) { mejor = v; n = c; }
    return n * 2 > valores.length ? mejor : null; // solo si es la mayoría
  };
  const redondea = (v: number) => Math.round(v / 0.05) * 0.05;
  const handles: string[] = [];
  const bloques = new Set<string>();
  const capas = new Set<string>();
  let seCotiza = false;
  for (const [crudo, g] of porBloque) {
    if (g.length < 2) continue;
    const lx = g.map((x) => (x.bloque!.sx !== 0 ? Math.log10(Math.abs(x.bloque!.sx)) : NaN));
    const ly = g.map((x) => (x.bloque!.sy !== 0 ? Math.log10(Math.abs(x.bloque!.sy)) : NaN));
    const mx = moda(lx.filter(Number.isFinite).map(redondea));
    const my = moda(ly.filter(Number.isFinite).map(redondea));
    const nombre = limpio.get(crudo) ?? crudo;
    g.forEach((x, i) => {
      const vx = lx[i] ?? NaN;
      const vy = ly[i] ?? NaN;
      const fueraX = mx != null && Number.isFinite(vx) && Math.abs(vx - mx) >= p.diag_bloque_escala_decadas;
      const fueraY = my != null && Number.isFinite(vy) && Math.abs(vy - my) >= p.diag_bloque_escala_decadas;
      if (!fueraX && !fueraY) return;
      handles.push(x.handle);
      bloques.add(nombre);
      capas.add(x.capa);
      if (bloqueSeCotiza(e, nombre)) seCotiza = true;
    });
  }
  // Pieza más grande que su propia vista (compara contra el centro de la vista; sin visor, contra su caja).
  const { nucleos } = e.inventario.vistas?.length ? repartirEnVistas(e.inventario, ents, p) : { nucleos: new Map<string, Nucleo>() };
  const vistas = new Map((e.inventario.vistas ?? []).map((v) => [v.id, v]));
  const desmedidos: string[] = [];
  for (const b of e.inventario.bloques) {
    const lado = Math.max(b.ancho ?? 0, b.alto ?? 0);
    if (!(lado > 0) || !b.por_vista) continue;
    const vid = Object.entries(b.por_vista).sort((a, c) => c[1] - a[1])[0]?.[0];
    const v = vid ? vistas.get(vid) : null;
    if (!v) continue;
    const nu = nucleos.get(v.id);
    const menor = nu ? Math.min(nu.ancho, nu.alto) : Math.min(v.caja[2] - v.caja[0], v.caja[3] - v.caja[1]);
    if (menor > 0 && lado >= (p.diag_bloque_vs_vista_pct / 100) * menor) desmedidos.push(b.nombre);
  }
  desmedidos.forEach((n) => bloques.add(n));
  const piezas = handles.length + desmedidos.length;
  if (!piezas) return null;
  return {
    clave: 'escala_de_bloque',
    gravedad: seCotiza || desmedidos.length ? 'media' : 'info',
    titulo: `${piezasTxt(piezas)} a otra escala que ${piezas === 1 ? 'las demás' : 'sus iguales'} (${lista([...bloques])})`,
    quePasa: 'Hay bloques dibujados mucho más grandes o más chicos que otros iguales, o más grandes que su propia planta.',
    porQueAfecta:
      'No calzan con su hueco en el muro, así que el muro no descuenta ese hueco; un bloque en otra unidad también agranda la planta.',
    queHacer: 'Revisa la escala de esas piezas en tu programa de dibujo.',
    piezas,
    capas: [...capas],
    bloques: [...bloques],
    handles: topeHandles(handles),
    impacto: null,
  };
}

/** V1-6 · Bloques con la misma forma pero distinto nombre (se cuentan en renglones separados). */
export function bloquesGemelos(e: EntradaDiagnostico, p: ParametrosChequeoPlano): AvisoDelDibujo | null {
  const hs = e.huellas;
  if (!hs?.length) return null;
  const porHuella = new Map<string, Set<string>>();
  for (const h of hs) {
    if (!h.huella || h.trazos < p.diag_gemelo_min_trazos || h.trazos > p.diag_bloque_huella_max_trazos) continue;
    const s = porHuella.get(h.huella) ?? new Set<string>();
    s.add(h.limpio);
    porHuella.set(h.huella, s);
  }
  const porNombre = new Map(e.inventario.bloques.map((b) => [b.nombre, b]));
  let piezas = 0;
  let distintas = false;
  const nombres: string[] = [];
  const handles: string[] = [];
  for (const s of porHuella.values()) {
    if (s.size < 2) continue;
    const ns = [...s];
    nombres.push(...ns);
    const cotiza = ns.map((n) => bloqueSeCotiza(e, n));
    if (cotiza.some((c) => c) && cotiza.some((c) => !c)) distintas = true;
    for (const n of ns) {
      const b = porNombre.get(n);
      if (b) {
        piezas += b.n;
        handles.push(...b.ids);
      }
    }
  }
  if (!nombres.length) return null;
  const dosPrimeros = [...new Set(nombres)].slice(0, 2).map((n) => `«${n}»`).join(' y ');
  return {
    clave: 'bloques_gemelos',
    gravedad: distintas ? 'alta' : 'info',
    titulo: `${dosPrimeros} parecen el mismo dibujo con dos nombres`,
    quePasa: 'Hay bloques con la misma forma que están guardados con nombres distintos.',
    porQueAfecta: 'Se cuentan en renglones separados; si solo uno tiene concepto, faltan piezas en la hoja.',
    queHacer: 'Si son lo mismo, asígnales el mismo concepto. Si son distintos (otra especificación), déjalos así.',
    piezas,
    capas: [],
    bloques: [...new Set(nombres)],
    handles: topeHandles(handles),
    impacto: null,
  };
}

// ───────────────────────────── orquestador ─────────────────────────────

const ORDEN_GRAVEDAD: Record<GravedadDeAviso, number> = { alta: 0, media: 1, info: 2 };

/** Corre todos los chequeos. Nunca tira: uno que falla simplemente no aparece. */
export function diagnosticarDibujo(e: EntradaDiagnostico, p: ParametrosChequeoPlano): DiagnosticoDelDibujo {
  const avisos: AvisoDelDibujo[] = [];
  const seguro = (f: () => AvisoDelDibujo | AvisoDelDibujo[] | null) => {
    try {
      const r = f();
      if (Array.isArray(r)) avisos.push(...r);
      else if (r) avisos.push(r);
    } catch {
      /* un chequeo que falla no tumba a los demás */
    }
  };
  seguro(() => unidadYCotas(e, p));
  seguro(() => piezasRepetidas(e, p));
  seguro(() => trazosRepetidos(e, p));
  seguro(() => piezasSueltas(e, p));
  seguro(() => escalaDeBloques(e, p));
  seguro(() => bloquesGemelos(e, p));
  avisos.sort((a, b) => ORDEN_GRAVEDAD[a.gravedad] - ORDEN_GRAVEDAD[b.gravedad] || b.piezas - a.piezas);
  return { avisos, entidadesRevisadas: e.entidades?.length ?? 0, parcial: e.entidades == null };
}
